import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation } from 'react-router-dom';
import { FeedbackForm } from '../types/index';
import { useAppSelector } from '../store/hooks';
import api from '../api';
import { FaHeadphones, FaClipboardList, FaUsers, FaChartLine, FaTrophy, FaMusic, FaRobot, FaCheckCircle } from 'react-icons/fa';
import { IoCheckmarkCircle } from 'react-icons/io5';
import { GiTargetShot, GiMeditation } from 'react-icons/gi';
import { PiBrainBold, PiWaveformFill, PiPlantFill } from 'react-icons/pi';
import { MdTimer, MdMenu, MdClose } from 'react-icons/md';
import { SiStreamrunners } from 'react-icons/si';
import PrivacyPolicyModal from '../components/PrivacyPolicyModal';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAppSelector((state) => state.user);
  const isBlogActive = location.pathname.startsWith('/blog');
  const [feedback, setFeedback] = useState<FeedbackForm>({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const handleFeedbackSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      await api.others.submitFeedback({
        name: feedback.name,
        email: feedback.email,
        message: feedback.message,
      });

      setShowToast(true);
      setFeedback({ name: '', email: '', message: '' });
      setTimeout(() => setShowToast(false), 3000);
    } catch (error: any) {
      console.error('Failed to submit feedback:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to submit feedback. Please try again.';
      setErrorMessage(errorMsg);
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    setHeroLoaded(true);
  }, []);

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>Focuzon - Best Focus App for Deep Work | Pomodoro Timer & Focus Sessions</title>
        <meta name="description" content="Focuzon is the best focus app for deep work and concentration. Free Pomodoro timer, focus sessions, team collaboration, and mindful breaks. Start your first focused work session today." />
        <meta name="keywords" content="focus app, best focus app, deep work app, pomodoro timer, focus timer, concentration app, productivity app, focus sessions, team focus, workplace focus, mindfulness app, quiet workspace, distraction free, focus music, binaural beats, focus sounds, time management, work timer, study timer, focus tool, deep work tool, focus workspace, team collaboration, shared focus, focus analytics, focus tracker, meditation app, calm workspace, focus environment" />
        <meta name="author" content="Focuzon" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href="https://www.focuzon.com/" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        <meta name="geo.region" content="US" />
        <meta name="geo.placename" content="United States" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.focuzon.com/" />
        <meta property="og:title" content="Focuzon - Best Focus App for Deep Work | Pomodoro Timer" />
        <meta property="og:description" content="Focuzon is the best focus app for deep work and concentration. Free Pomodoro timer, focus sessions, team collaboration, and mindful breaks." />
        <meta property="og:image" content="https://www.focuzon.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Focuzon - Focus App for Deep Work" />
        <meta property="og:site_name" content="Focuzon" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.focuzon.com/" />
        <meta name="twitter:title" content="Focuzon - Best Focus App for Deep Work | Pomodoro Timer" />
        <meta name="twitter:description" content="Focuzon is the best focus app for deep work and concentration. Free Pomodoro timer, focus sessions, team collaboration, and mindful breaks." />
        <meta name="twitter:image" content="https://www.focuzon.com/og-image.jpg" />
        <meta name="twitter:image:alt" content="Focuzon - Focus App for Deep Work" />
        <meta name="twitter:site" content="@focuzon" />
        <meta name="twitter:creator" content="@focuzon" />

        {/* Structured Data - SoftwareApplication */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Focuzon",
            "alternateName": "Focus Mode AI",
            "description": "Focuzon is the best focus app for deep work and concentration. Free Pomodoro timer, focus sessions, team collaboration, and mindful breaks designed around how focus actually works.",
            "url": "https://www.focuzon.com",
            "applicationCategory": ["ProductivityApplication", "BusinessApplication"],
            "operatingSystem": ["Web", "iOS", "Android"],
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "1250",
              "bestRating": "5",
              "worstRating": "1"
            },
            "featureList": [
              "Pomodoro Timer",
              "Focus Sessions",
              "Team Collaboration",
              "Focus Sounds & Music",
              "Mindful Breaks",
              "Session Notes",
              "Focus Analytics",
              "Calming Activities"
            ],
            "screenshot": "https://www.focuzon.com/og-image.jpg",
            "softwareVersion": "1.0",
            "releaseNotes": "A quiet workspace for deep, focused work",
            "browserRequirements": "Requires JavaScript. Requires HTML5.",
            "softwareHelp": {
              "@type": "CreativeWork",
              "url": "https://www.focuzon.com/about"
            }
          })}
        </script>

        {/* Structured Data - Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Focuzon",
            "alternateName": "Focus Mode AI",
            "url": "https://www.focuzon.com",
            "logo": "https://www.focuzon.com/focuzon.png",
            "description": "Focuzon provides a quiet workspace for deep, focused work. We build tools designed around how focus actually works.",
            "foundingDate": "2024",
            "contactPoint": {
              "@type": "ContactPoint",
              "email": "support@focuzon.com",
              "contactType": "Customer Support"
            },
            "sameAs": [
              "https://twitter.com/focuzon",
              "https://github.com/focuzon"
            ],
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "US"
            }
          })}
        </script>

        {/* Structured Data - FAQPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is Focuzon?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Focuzon is a focus app designed for deep work and concentration. It combines Pomodoro timer functionality, focus sessions, team collaboration, and mindful breaks in one calm workspace."
                }
              },
              {
                "@type": "Question",
                "name": "Is Focuzon free?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, Focuzon is completely free to use. Start your first focus session today without any cost."
                }
              },
              {
                "@type": "Question",
                "name": "How does Focuzon help with focus?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Focuzon helps you focus by providing a quiet workspace with Pomodoro timers, focus sounds, mindful breaks, and team collaboration features. It's designed around how focus actually works, not productivity hype."
                }
              },
              {
                "@type": "Question",
                "name": "Can I use Focuzon with my team?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, Focuzon supports team collaboration with shared focus sessions. Work together in aligned focus time without meetings or noise."
                }
              },
              {
                "@type": "Question",
                "name": "What makes Focuzon different from other focus apps?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Focuzon is designed as a quiet workspace, not a productivity system. We focus on calm, sustained attention rather than gamification, badges, or competitive elements. It's meditation-retreat-meets-workspace."
                }
              }
            ]
          })}
        </script>

        {/* Structured Data - WebSite */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Focuzon",
            "alternateName": "Focus Mode AI",
            "url": "https://www.focuzon.com",
            "description": "Best focus app for deep work and concentration",
            "inLanguage": "en-US",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://www.focuzon.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>

        {/* Structured Data - BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.focuzon.com/"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#FFF8F0] relative overflow-hidden" itemScope itemType="https://schema.org/WebPage">
        {/* Enhanced Orange Gradient Background Shapes */}
        <div className="fixed inset-0 pointer-events-none z-0">
          <div className="absolute top-0 -left-1/4 w-96 h-96 bg-gradient-to-br from-orange-300/50 to-orange-200/40 rounded-full blur-3xl animate-blob-slow"></div>
          <div className="absolute top-1/4 -right-1/4 w-96 h-96 bg-gradient-to-br from-orange-400/50 to-orange-300/40 rounded-full blur-3xl animate-blob-slow" style={{ animationDelay: "5s" }}></div>
          <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-gradient-to-br from-orange-200/40 to-orange-100/30 rounded-full blur-3xl animate-blob-slow" style={{ animationDelay: "10s" }}></div>
          <div className="absolute top-1/2 right-1/3 w-72 h-72 bg-gradient-to-br from-orange-300/40 to-orange-200/30 rounded-full blur-3xl animate-blob-slow" style={{ animationDelay: "15s" }}></div>
        </div>

        {/* Navigation with Orange Theme - Fixed Header */}
        <nav className="fixed top-0 left-0 right-0 z-50 border-b border-orange-100 bg-[#FFF8F0]/80 backdrop-blur-sm" role="navigation" aria-label="Main navigation">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Logo - Left Aligned */}
              <div className="flex items-center justify-center flex-shrink-0 h-full">
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/');
                  }}
                  aria-label="Focuzon Home - Best Focus App"
                  className="flex items-center"
                >
                  <img
                    src="/focuzon.png"
                    alt="Focuzon - Best Focus App for Deep Work and Concentration | Pomodoro Timer"
                    title="Focuzon - Best Focus App"
                    className="h-8 w-auto object-contain cursor-pointer hover:opacity-90 transition-opacity"
                    style={{ maxWidth: '140px', minWidth: '100px' }}
                    loading="eager"
                    width="140"
                    height="32"
                  />
                </a>
              </div>

              {/* Navigation Menu - Centered */}
              <div className="hidden lg:flex items-center justify-center flex-1">
                <div className="flex items-center space-x-1 bg-white/60 backdrop-blur-sm rounded-full px-2 py-1.5 border border-orange-100/50 shadow-sm">
                  <button
                    onClick={() => navigate('/')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${location.pathname === '/' || location.pathname === ''
                      ? 'text-orange-600 bg-orange-50 shadow-sm'
                      : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50/50'
                      }`}
                  >
                    Home
                  </button>
                  <div className="w-px h-4 bg-orange-200/50"></div>
                  <button
                    onClick={() => navigate('/blog')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${isBlogActive
                      ? 'text-orange-600 bg-orange-50 shadow-sm'
                      : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50/50'
                      }`}
                  >
                    Blog
                  </button>
                  <div className="w-px h-4 bg-orange-200/50"></div>
                  <button
                    onClick={() => navigate('/about')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${location.pathname === '/about'
                      ? 'text-orange-600 bg-orange-50 shadow-sm'
                      : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50/50'
                      }`}
                  >
                    About Us
                  </button>
                </div>
              </div>

              {/* Auth Buttons - Right Aligned */}
              <div className="flex items-center space-x-3 flex-shrink-0">
                {!isAuthenticated ? (
                  <>
                    <button
                      onClick={() => navigate('/login')}
                      className="hidden sm:block text-gray-700 hover:text-orange-600 font-medium transition-colors text-sm px-3 py-1.5 rounded-lg hover:bg-orange-50/50"
                    >
                      Log in
                    </button>
                    <button
                      onClick={() => navigate('/signup')}
                      className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-5 py-2 rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg shadow-orange-200 text-sm whitespace-nowrap"
                    >
                      Sign up
                    </button>
                  </>
                ) : (
                  <button
                    onClick={goToDashboard}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-5 py-2 rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg shadow-orange-200 text-sm whitespace-nowrap"
                  >
                    Go to Dashboard
                  </button>
                )}

                {/* Mobile Menu Button */}
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="lg:hidden text-gray-700 hover:text-orange-600 transition-colors p-2 rounded-lg hover:bg-orange-50/50"
                  aria-label="Toggle menu"
                >
                  {showMobileMenu ? (
                    <MdClose className="w-6 h-6" />
                  ) : (
                    <MdMenu className="w-6 h-6" />
                  )}
                </button>
              </div>
            </div>

            {/* Mobile Menu */}
            {showMobileMenu && (
              <div className="lg:hidden border-t border-orange-100 py-4 bg-white/60 backdrop-blur-sm">
                <div className="flex flex-col space-y-2 px-2">
                  <button
                    onClick={() => {
                      navigate('/');
                      setShowMobileMenu(false);
                    }}
                    className={`text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${location.pathname === '/' || location.pathname === ''
                      ? 'text-orange-600 bg-orange-50'
                      : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50/50'
                      }`}
                  >
                    Home
                  </button>
                  <button
                    onClick={() => {
                      navigate('/blog');
                      setShowMobileMenu(false);
                    }}
                    className={`text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${isBlogActive
                      ? 'text-orange-600 bg-orange-50'
                      : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50/50'
                      }`}
                  >
                    Blog
                  </button>
                  <button
                    onClick={() => {
                      navigate('/about');
                      setShowMobileMenu(false);
                    }}
                    className={`text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${location.pathname === '/about'
                      ? 'text-orange-600 bg-orange-50'
                      : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50/50'
                      }`}
                  >
                    About Us
                  </button>
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Hero Section - Focus-First Workspace */}
        <section id="what" className="relative z-10 pt-40 pb-48 min-h-screen flex items-center bg-[#FFF8F0] overflow-hidden" itemScope itemType="https://schema.org/WebPageElement">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-[20%] left-[10%] w-64 h-64 bg-orange-200/40 rounded-full mix-blend-multiply filter blur-3xl opacity-60 animate-blob"></div>
            <div className="absolute top-[30%] right-[10%] w-72 h-72 bg-amber-200/40 rounded-full mix-blend-multiply filter blur-3xl opacity-60 animate-blob" style={{ animationDelay: "2s" }}></div>
            <div className="absolute -bottom-8 left-[30%] w-80 h-80 bg-rose-200/40 rounded-full mix-blend-multiply filter blur-3xl opacity-60 animate-blob" style={{ animationDelay: "4s" }}></div>

            {/* Floating Shapes */}
            <div className="absolute top-[15%] right-[20%] w-16 h-16 border-4 border-orange-300/20 rounded-full animate-float opacity-50"></div>
            <div className="absolute bottom-[20%] left-[15%] w-12 h-12 border-4 border-amber-300/20 rounded-full animate-float opacity-50" style={{ animationDelay: "2s" }}></div>

            {/* Additional Elements */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-orange-100/30 rounded-full animate-pulse-slow pointer-events-none"></div>
            <div className="absolute top-1/3 left-1/4 w-2 h-2 bg-orange-400/40 rounded-full animate-float" style={{ animationDelay: "1.5s" }}></div>
            <div className="absolute top-2/3 right-1/3 w-3 h-3 bg-amber-400/40 rounded-full animate-float" style={{ animationDelay: "3.5s" }}></div>
            <div className="absolute bottom-1/4 right-1/4 w-2 h-2 bg-rose-400/40 rounded-full animate-float" style={{ animationDelay: "0.5s" }}></div>
          </div>

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1
              className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-12 text-gray-900 leading-tight uppercase transition-all duration-700 ease-out ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[12px]'
                }`}
            >
              A Digital Workspace Built for Deep Focus
            </h1>

            <div className="flex flex-wrap items-center justify-center gap-2.5 mt-8 text-base sm:text-lg text-gray-600">
              <span
                className={`transition-all duration-700 ease-out delay-[120ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[12px]'
                  }`}
              >
                All your
              </span>
              <span
                className={`px-4 py-2 rounded-full border border-orange-400/50 bg-orange-50/5 text-orange-600/90 text-sm font-medium hover:opacity-80 transition-all duration-700 ease-out delay-[150ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[12px]'
                  }`}
              >
                Focus sessions
              </span>
              <span
                className={`px-4 py-2 rounded-full border border-blue-400/50 bg-blue-50/5 text-blue-600/90 text-sm font-medium hover:opacity-80 transition-all duration-700 ease-out delay-[200ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[12px]'
                  }`}
              >
                Session notes
              </span>
              <span
                className={`px-4 py-2 rounded-full border border-violet-400/50 bg-violet-50/5 text-violet-600/90 text-sm font-medium hover:opacity-80 transition-all duration-700 ease-out delay-[250ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[12px]'
                  }`}
              >
                Shared focus
              </span>
              <span
                className={`px-4 py-2 rounded-full border border-teal-400/50 bg-teal-50/5 text-teal-600/90 text-sm font-medium hover:opacity-80 transition-all duration-700 ease-out delay-[300ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[12px]'
                  }`}
              >
                Focus sounds
              </span>
              <span
                className={`px-4 py-2 rounded-full border border-amber-400/50 bg-amber-50/5 text-amber-600/90 text-sm font-medium hover:opacity-80 transition-all duration-700 ease-out delay-[350ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[12px]'
                  }`}
              >
                Breaks
              </span>
              <span
                className={`px-4 py-2 rounded-full border border-emerald-400/50 bg-emerald-50/5 text-emerald-600/90 text-sm font-medium hover:opacity-80 transition-all duration-700 ease-out delay-[400ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[12px]'
                  }`}
              >
                Insights
              </span>
              <span
                className={`transition-all duration-700 ease-out delay-[450ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[12px]'
                  }`}
              >
                thoughtfully combined in one calm, private workspace.
              </span>
            </div>

            {/* Scroll Indicator */}
            <div
              className={`absolute bottom-10 left-1/2 transform -translate-x-1/2 cursor-pointer transition-all duration-1000 delay-[800ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              onClick={() => scrollToSection('why')}
            >
              <div className="w-[35px] h-[64px] rounded-3xl border-4 border-orange-400/30 flex justify-center items-start p-2">
                <div className="w-1.5 h-1.5 bg-orange-400 rounded-full animate-bounce mb-1"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Section */}
        <section id="why" className="relative z-10 py-24 bg-white" itemScope itemType="https://schema.org/WebPageElement">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-medium mb-6 text-gray-900">
                Designed Around How Focus Actually Works
              </h2>
              <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                A calmer way to work: built to support deep attention, healthy pacing, and meaningful collaboration.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: <FaRobot className="text-3xl text-orange-500" />,
                  title: 'Focus, Guided Naturally',
                  description: 'Smart session flow and gentle break reminders: designed to protect your attention, not interrupt it.'
                },
                {
                  icon: <MdTimer className="text-3xl text-orange-500" />,
                  title: 'Work at Your Own Rhythm',
                  description: 'Choose session lengths that fit how you think: short sprints, long deep work, or custom focus cycles.'
                },
                {
                  icon: <FaUsers className="text-3xl text-orange-500" />,
                  title: 'Focus Alone or Together',
                  description: 'Enter shared focus sessions with your team: no meetings, no noise, just aligned work time.'
                }
              ].map((item, index) => (
                <div
                  key={index}
                  className="text-center p-10 sm:p-12 bg-orange-50/30 rounded-2xl border border-orange-100/60 hover:-translate-y-1 hover:shadow-sm transition-all duration-300 ease-out"
                >
                  <div className="flex justify-center mb-6">{item.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how" className="relative z-10 py-24 bg-orange-50/30" itemScope itemType="https://schema.org/HowTo">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-medium mb-6 text-gray-900">
                Entering Focus, Step by Step
              </h2>
              <p className="text-lg sm:text-xl text-gray-600">Get started in three simple moments.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  step: '1',
                  title: 'Name What Matters',
                  description: 'Set a clear intention for this session: work, study, or creative thinking.',
                  icon: <GiTargetShot className="text-4xl text-orange-500" />
                },
                {
                  step: '2',
                  title: 'Choose Your Focus Window',
                  description: 'Select a session length that fits your rhythm: short sprints, deep stretches, or custom time.',
                  icon: <MdTimer className="text-4xl text-orange-500" />
                },
                {
                  step: '3',
                  title: 'Settle Into Focus',
                  description: 'Start your session and let Focuzon support you with focus sounds, gentle breaks, and reminders.',
                  icon: <SiStreamrunners className="text-4xl text-orange-500" />
                }
              ].map((item, index) => (
                <div
                  key={index}
                  className={`text-center bg-white rounded-2xl p-10 sm:p-12 border border-orange-100/60 transition-all duration-700 ease-out ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[12px]'
                    }`}
                  style={{ transitionDelay: `${150 + (index * 100)}ms` }}
                >
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-50/50 rounded-full mb-6">
                    {item.icon}
                  </div>
                  <div className="text-orange-500 font-semibold text-xl mb-3">Step {item.step}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-5">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="relative z-10 py-24 bg-white" itemScope itemType="https://schema.org/ItemList">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-medium mb-6 text-gray-900">
                Support for Deep, Focused Work
              </h2>
              <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
                A calm set of capabilities that support focus without distraction.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: <FaHeadphones className="text-3xl text-orange-500" />,
                  title: 'Focus Sounds',
                  description: 'A quiet environment with nature sounds, binaural beats, or your own music: designed to support concentration.'
                },
                {
                  icon: <GiMeditation className="text-3xl text-orange-500" />,
                  title: 'Smart Breaks',
                  description: 'Gentle reminders for your eyes, posture, and mind: helping you maintain focus without interruption.'
                },
                {
                  icon: <FaClipboardList className="text-3xl text-orange-500" />,
                  title: 'Session Notes',
                  description: 'Capture thoughts and ideas as they arise: integrated naturally into your focus flow.'
                },
                {
                  icon: <FaUsers className="text-3xl text-orange-500" />,
                  title: 'Shared Focus',
                  description: 'Enter aligned work time with your team: shared focus sessions without meetings or noise.'
                },
                {
                  icon: <FaChartLine className="text-3xl text-orange-500" />,
                  title: 'Focus Insights',
                  description: 'Quiet observation of your focus patterns: understanding your rhythm without pressure or metrics.'
                },
                {
                  icon: <GiMeditation className="text-3xl text-orange-500" />,
                  title: 'Mindful Practices, Not Distractions',
                  description: 'Simple tools to reset your attention: breathing exercises, gentle puzzles, and calming interactions. Use them when helpful, ignore them when not. No competition, no pressure.'
                }
              ].map((feature, index) => (
                <div key={index} className="bg-orange-50/30 rounded-xl p-8 border border-orange-100/60 hover:-translate-y-1 hover:shadow-sm transition-all duration-300 ease-out">
                  <div className="mb-5">{feature.icon}</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section - SEO Optimized */}
        <section id="faq" className="relative z-10 py-24 bg-white" itemScope itemType="https://schema.org/FAQPage">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-medium mb-6 text-gray-900">
                Frequently Asked Questions
              </h2>
              <p className="text-lg sm:text-xl text-gray-600">
                Everything you need to know about Focuzon
              </p>
            </div>

            <div className="space-y-6">
              <article className="bg-orange-50/30 rounded-xl p-8 border border-orange-100/60">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">What is Focuzon?</h3>
                <p className="text-gray-600 leading-relaxed">
                  Focuzon is a focus app designed for deep work and concentration. It combines Pomodoro timer functionality, focus sessions, team collaboration, and mindful breaks in one calm workspace. Unlike other productivity apps, Focuzon is designed around how focus actually works, not productivity hype.
                </p>
              </article>

              <article className="bg-orange-50/30 rounded-xl p-8 border border-orange-100/60">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Is Focuzon free?</h3>
                <p className="text-gray-600 leading-relaxed">
                  Yes, Focuzon is completely free to use. Start your first focus session today without any cost. No credit card required, no hidden fees.
                </p>
              </article>

              <article className="bg-orange-50/30 rounded-xl p-8 border border-orange-100/60">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">How does Focuzon help with focus?</h3>
                <p className="text-gray-600 leading-relaxed">
                  Focuzon helps you focus by providing a quiet workspace with Pomodoro timers, focus sounds (nature sounds, binaural beats), mindful breaks, and team collaboration features. It's designed around how focus actually works—supporting sustained attention without gamification or pressure.
                </p>
              </article>

              <article className="bg-orange-50/30 rounded-xl p-8 border border-orange-100/60">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Can I use Focuzon with my team?</h3>
                <p className="text-gray-600 leading-relaxed">
                  Yes, Focuzon supports team collaboration with shared focus sessions. Work together in aligned focus time without meetings or noise. Perfect for remote teams and co-working spaces.
                </p>
              </article>

              <article className="bg-orange-50/30 rounded-xl p-8 border border-orange-100/60">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">What makes Focuzon different from other focus apps?</h3>
                <p className="text-gray-600 leading-relaxed">
                  Focuzon is designed as a quiet workspace, not a productivity system. We focus on calm, sustained attention rather than gamification, badges, or competitive elements. It's meditation-retreat-meets-workspace—a calm set of capabilities designed around how focus actually works.
                </p>
              </article>

              <article className="bg-orange-50/30 rounded-xl p-8 border border-orange-100/60">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Do I need to download anything?</h3>
                <p className="text-gray-600 leading-relaxed">
                  No, Focuzon works entirely in your web browser. It's a Progressive Web App (PWA), so you can use it on any device—desktop, tablet, or mobile. You can also install it as an app if you prefer.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative z-10 py-32 bg-[#FFF8F0]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl sm:text-5xl font-medium mb-6 text-gray-900">
              Start Your First Focus Session
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 mb-12 max-w-2xl mx-auto leading-relaxed">
              Experience what deep, focused work feels like: without setup, pressure, or distractions.
            </p>
            <div className="flex flex-col items-center gap-4">
              <button
                onClick={goToDashboard}
                className="bg-orange-600 text-white px-10 py-4 rounded-lg text-lg font-medium hover:bg-orange-700 transition-colors duration-200"
                aria-label="Start a focus session with Focuzon"
              >
                Start a Focus Session
              </button>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="relative z-10 py-24 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-medium text-center mb-4 text-gray-900">
              Help Shape Focuzon
            </h2>
            <p className="text-center text-gray-600 mb-12 text-lg">
              If you have thoughts, ideas, or quiet frustrations about focus, we're listening.
            </p>

            <div className="bg-[#FFF8F0] rounded-xl p-10 border border-orange-100/40">
              <form onSubmit={handleFeedbackSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="How should we address you?"
                    value={feedback.name}
                    onChange={(e) => setFeedback({ ...feedback, name: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-orange-200/50 rounded-lg focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400/70 focus:outline-none text-gray-950 placeholder:text-gray-500 placeholder:opacity-50 transition-all duration-200"
                  />
                  <input
                    type="email"
                    placeholder="Email (only if you'd like a reply)"
                    value={feedback.email}
                    onChange={(e) => setFeedback({ ...feedback, email: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-orange-200/50 rounded-lg focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400/70 focus:outline-none text-gray-950 placeholder:text-gray-500 placeholder:opacity-50 transition-all duration-200"
                  />
                </div>
                <textarea
                  placeholder="What's on your mind? A small idea, a rough thought, or something that broke your focus today."
                  rows={6}
                  value={feedback.message}
                  onChange={(e) => setFeedback({ ...feedback, message: e.target.value })}
                  className="w-full px-5 py-4 bg-white border border-orange-200/50 rounded-lg focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400/70 focus:outline-none resize-none text-gray-950 placeholder:text-gray-500 placeholder:opacity-50 transition-all duration-200"
                  required
                ></textarea>
                {errorMessage && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
                    {errorMessage}
                  </div>
                )}
                <div className="space-y-3">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-orange-600 text-white px-6 py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-orange-700 transition-colors duration-200"
                  >
                    {isSubmitting ? 'Sending...' : 'Share Your Thoughts'}
                  </button>
                  <p className="text-center text-sm text-gray-500">We read every message. No automated replies.</p>
                </div>
              </form>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="relative z-10 bg-gray-900 text-gray-300 border-t border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
              {/* Brand */}
              <div>
                <div className="flex items-center mb-4">
                  <img
                    src="/focuzon.png"
                    alt="Focuzon Logo"
                    className="h-8 w-auto object-contain"
                    style={{ maxWidth: '140px', minWidth: '100px' }}
                  />
                </div>
                <p className="text-sm text-gray-400">
                  A workspace designed for deep, focused work: for individuals and teams.
                </p>
              </div>

              {/* Product */}
              <div>
                <h3 className="text-white font-semibold mb-4">Product</h3>
                <ul className="space-y-2 text-sm">
                  <li><a href="#what" className="hover:text-orange-400 transition-colors">Features</a></li>
                  <li><a href="#how" className="hover:text-orange-400 transition-colors">How It Works</a></li>
                  <li><a href="#why" className="hover:text-orange-400 transition-colors">Why Focuzon</a></li>
                </ul>
              </div>

              {/* Company */}
              <div>
                <h3 className="text-white font-semibold mb-4">Company</h3>
                <ul className="space-y-2 text-sm">
                  <li><button onClick={() => navigate('/about')} className="hover:text-orange-400 transition-colors text-left">About</button></li>
                  <li><button onClick={() => navigate('/blog')} className="hover:text-orange-400 transition-colors text-left">Blog</button></li>
                  <li><button onClick={() => scrollToSection('contact')} className="hover:text-orange-400 transition-colors text-left">Contact</button></li>
                </ul>
              </div>

              {/* Legal */}
              <div>
                <h3 className="text-white font-semibold mb-4">Legal</h3>
                <ul className="space-y-2 text-sm">
                  <li>
                    <button
                      onClick={() => setShowPrivacyModal(true)}
                      className="hover:text-orange-400 transition-colors text-left"
                    >
                      Privacy Policy
                    </button>
                  </li>
                  <li><a href="#" className="hover:text-orange-400 transition-colors">Terms of Service</a></li>
                  <li><a href="#" className="hover:text-orange-400 transition-colors">Cookie Policy</a></li>
                  <li><a href="#" className="hover:text-orange-400 transition-colors">GDPR</a></li>
                </ul>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
              <p className="text-sm text-gray-400 mb-4 md:mb-0">
                © 2025 Focuzon, Inc. All rights reserved.
              </p>
              <div className="flex items-center space-x-6">
                <a href="#" className="text-gray-400 hover:text-orange-400 transition-colors">
                  <span className="sr-only">Twitter</span>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-orange-400 transition-colors">
                  <span className="sr-only">GitHub</span>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-orange-400 transition-colors">
                  <span className="sr-only">LinkedIn</span>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </footer>

        {/* Toast Notification for Feedback */}
        {showToast && (
          <div className="fixed bottom-4 left-4 z-50 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-2">
            <IoCheckmarkCircle className="text-white text-xl" />
            <span>Feedback submitted successfully!</span>
          </div>
        )}

        <PrivacyPolicyModal
          isOpen={showPrivacyModal}
          onClose={() => setShowPrivacyModal(false)}
        />
      </div>
    </>
  );
};

export default LandingPage;
